import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { fDateTime } from 'src/utils/format-time';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

// ----------------------------------------------------------------------

// Status color mapping
const STATUS_CONFIG = {
  Reviewing: {
    color: '#0288D1',
    variant: 'info',
    label: 'Reviewing',
    icon: 'solar:eye-bold-duotone',
  },
  Replied: {
    color: '#2E7D32',
    variant: 'success',
    label: 'Replied',
    icon: 'solar:check-circle-bold-duotone',
  },
  Closed: {
    color: '#637381',
    variant: 'default',
    label: 'Closed',
    icon: 'solar:close-circle-bold-duotone',
  },
};

// Feedback type icon mapping
const FEEDBACK_TYPE_ICONS = {
  'Bug Report': 'solar:bug-bold-duotone',
  'Feature Request': 'solar:lightbulb-bolt-bold-duotone',
  'General Feedback': 'solar:chat-round-dots-bold-duotone',
  'Security Concern': 'solar:shield-warning-bold-duotone',
  'Performance Issue': 'solar:speedometer-bold-duotone',
  'UI/UX Feedback': 'solar:palette-bold-duotone',
};

// ----------------------------------------------------------------------

export function FeedbackDetail({ feedback, onBack }) {
  const statusConfig = STATUS_CONFIG[feedback.Status] || STATUS_CONFIG.Reviewing;
  const feedbackTypeIcon = feedback.FeedbackType?.Icon || 'solar:chat-round-dots-bold-duotone';
  const feedbackTypeIconColorCode = feedback.FeedbackType?.ColorCode || '#000000';
  const hasResponses = feedback.Replies && feedback.Replies.length > 0;

  const renderHeader = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2, px: 2.5, minHeight: 68 }}>
      <IconButton onClick={onBack}>
        <Iconify icon="eva:arrow-ios-back-fill" />
      </IconButton>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Feedback Details
      </Typography>
    </Stack>
  );

  const renderFeedbackInfo = (
    <Box sx={{ px: 2.5, pb: 2 }}>
      {/* Type and Status */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: `${statusConfig.color}15`,
            color: statusConfig.color,
          }}
        >
          <Iconify icon={feedbackTypeIcon} color={feedbackTypeIconColorCode} width={32} />
        </Stack>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {feedback.FeedbackType?.Name || 'Feedback'}
          </Typography>
          <Chip
            icon={<Iconify icon={statusConfig.icon} width={16} />}
            label={statusConfig.label}
            size="small"
            variant="filled"
            sx={{
              bgcolor: `${statusConfig.color}20`,
              color: statusConfig.color,
              fontWeight: 600,
            }}
          />
        </Box>
      </Stack>

      {/* Description */}
      {feedback.FeedbackType?.Description && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.neutral', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {feedback.FeedbackType.Description}
          </Typography>
        </Box>
      )}

      {/* Metadata */}
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="solar:calendar-bold-duotone" width={20} color="text.secondary" />
          <Typography variant="body2" color="text.secondary">
            Submitted on {fDateTime(feedback.CreateDate)}
          </Typography>
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Message */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Your Message
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.neutral',
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.vars.palette.divider}`,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {feedback.Message}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderResponses = hasResponses && (
    <Box sx={{ px: 2.5, pb: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Iconify icon="solar:chat-line-bold-duotone" width={24} color="success.main" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Replies ({feedback.Replies.length})
        </Typography>
      </Stack>
      <Stack spacing={2}>
        {feedback.Replies.map((reply) => (
          <Box
            key={reply.Id}
            sx={{
              p: 2,
              bgcolor: 'success.lighter',
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.vars.palette.success.light}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  color: 'common.white',
                }}
              >
                <Iconify icon="solar:user-bold" width={18} />
              </Stack>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Support Team
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {fDateTime(reply.CreateDate)}
                </Typography>
              </Box>
            </Stack>
            <Typography variant="body2" sx={{ pl: 5, whiteSpace: 'pre-wrap' }}>
              {reply.Message}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {renderHeader}

      <Scrollbar sx={{ flexGrow: 1 }}>
        {renderFeedbackInfo}
        {renderResponses}
      </Scrollbar>

      <Box sx={{ p: 2, borderTop: (theme) => `solid 1px ${theme.vars.palette.divider}` }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Iconify icon="solar:arrow-left-bold" />}
          onClick={onBack}
        >
          Back to Feedbacks
        </Button>
      </Box>
    </Box>
  );
}

FeedbackDetail.propTypes = {
  feedback: PropTypes.shape({
    Id: PropTypes.number,
    Message: PropTypes.string,
    CreateDate: PropTypes.string,
    Status: PropTypes.string,
    Replies: PropTypes.arrayOf(
      PropTypes.shape({
        Id: PropTypes.number,
        Message: PropTypes.string,
        CreateDate: PropTypes.string,
      })
    ),
    FeedbackType: PropTypes.shape({
      Id: PropTypes.number,
      Name: PropTypes.string,
      Description: PropTypes.string,
      SortId: PropTypes.number,
    }),
    FirstName: PropTypes.string,
    LastName: PropTypes.string,
    Role: PropTypes.string,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
};
